import * as Popover from '@radix-ui/react-popover';
import { ArrowRight, Check, HelpCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/i18n';

export interface HelpContent {
  title: string;
  what: string;
  how: string;
  exampleLabel: string;
  exampleFrom: string;
  exampleTo: string;
  tips: readonly string[];
  warn: string;
}

/**
 * The explanation lives next to the field, one click away, and answers three
 * questions in a fixed order: what is it, how is it written, what does a good
 * one look like.
 *
 * A popover rather than a tooltip because this is content a person reads once
 * and then stops needing — a hover tooltip that vanishes when you move toward
 * the field it describes is worse than nothing. The bad → good example pair
 * does most of the teaching; the prose is there for the first read.
 */
export function HelpTip({ content, label }: { content: HelpContent; label?: string }) {
  const t = useT();

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label ?? content.title}
          className={cn(
            'inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full',
            'text-faint transition-ui duration-instant ease-out',
            'hover:bg-brand-500/10 hover:text-brand-500 active:scale-95',
            'data-[state=open]:bg-brand-500/12 data-[state=open]:text-brand-500',
            'dark:hover:text-brand-300 dark:data-[state=open]:text-brand-300',
          )}
        >
          <HelpCircle aria-hidden className="h-[0.875rem] w-[0.875rem]" strokeWidth={1.75} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={10}
          collisionPadding={16}
          className={cn(
            'z-50 w-[min(22rem,calc(100vw-2rem))]',
            // Outer tray → inner sheet, so the panel reads as a physical card
            // rather than a floating rectangle.
            'rounded-tray border border-line bg-raised p-1.5 shadow-pop',
            'data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out',
          )}
        >
          <div className="rounded-panel border border-line/70 bg-surface">
            <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
              <p className="font-display text-[0.9375rem] font-bold leading-snug text-strong">
                {content.title}
              </p>
              <Popover.Close
                aria-label={t.common.close}
                className="-mr-1 -mt-0.5 cursor-pointer rounded-control p-1 text-faint transition-ui duration-instant hover:bg-raised hover:text-strong"
              >
                <X aria-hidden className="h-3.5 w-3.5" />
              </Popover.Close>
            </div>

            <div className="flex flex-col gap-3.5 px-4 py-3.5">
              <p className="text-ui leading-relaxed text-muted">{content.what}</p>

              <p className="flex items-start gap-2 text-ui leading-relaxed text-strong">
                <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500 dark:text-brand-300" strokeWidth={1.75} />
                {content.how}
              </p>

              {/* The example pair carries most of the meaning. */}
              <div className="rounded-panel border border-line bg-raised px-3 py-2.5">
                <p className="eyebrow mb-2">{content.exampleLabel}</p>
                <p className="flex items-start gap-2 text-ui leading-snug text-faint line-through decoration-faint/60">
                  <X aria-hidden className="mt-0.5 h-3 w-3 shrink-0 no-underline" strokeWidth={2} />
                  <span>{content.exampleFrom}</span>
                </p>
                <p className="mt-1.5 flex items-start gap-2 text-ui leading-snug text-strong">
                  <ArrowRight aria-hidden className="mt-0.5 h-3 w-3 shrink-0 text-grass-500" strokeWidth={2} />
                  <span className="font-medium">{content.exampleTo}</span>
                </p>
              </div>

              {content.tips.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                  {content.tips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-ui leading-snug text-muted">
                      <Check aria-hidden className="mt-0.5 h-3 w-3 shrink-0 text-brand-500 dark:text-brand-300" strokeWidth={2.5} />
                      {tip}
                    </li>
                  ))}
                </ul>
              )}

              {content.warn && (
                <p className="rounded-panel border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-ui leading-snug text-amber-600 dark:text-amber-400">
                  {content.warn}
                </p>
              )}
            </div>
          </div>

          <Popover.Arrow className="fill-line" width={12} height={6} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
